import { gql, useQuery } from '@apollo/client';

import LoadingIndicator from '../../components/base/LoadingIndicator';
import { CalloutPaneBody } from '../../components/CalloutCarousel';
import MonthlySpendComparisonChart from '../../components/charts/MonthlySpendComparisonChart';
import DashboardAccountsWidget from '../../components/DashboardAccountsWidget';
import Entity from '../../components/entity';
import PlaidLinkButton from '../../components/plaid/PlaidLinkButton';
import TransactionsTable, { TxnTableElementType } from '../../components/tables/TransactionsTable';
import { useFilteredTransactions } from '../../hooks/useFilteredTransactions';
import logger from '../../lib/logger';
import { useAuthState } from '../../providers/AuthProvider';

import type { BankConnectionCheckQuery, BankConnectionCheckQueryVariables } from './__generated__/DashboardPane.gql';

const GET_BANK_CONNECTIONS = gql`
  query BankConnectionCheck {
    getBankConnectionsForUser {
      id
    }
  }
`;

const DashboardPane = () => {
  const { user, loading: authLoading } = useAuthState();
  const { transactions, loading: transactionsLoading } = useFilteredTransactions();

  const {
    data: bankConnectionsData,
    refetch: refetchBankConnections,
    loading: bankConnectionsLoading,
  } = useQuery<BankConnectionCheckQuery, BankConnectionCheckQueryVariables>(GET_BANK_CONNECTIONS);

  if (authLoading || bankConnectionsLoading) {
    return <LoadingIndicator fullScreen />;
  }

  const plaidConnected = bankConnectionsData?.getBankConnectionsForUser?.length;

  if (!plaidConnected) {
    return (
      <div className="text-primary flex min-h-screen w-full flex-col items-center justify-center gap-4">
        <PlaidLinkButton
          onSuccess={() => {
            refetchBankConnections().catch((err) => {
              logger.error('Failed to refetch bank connections:', err);
            });
          }}
        >
          Connect your accounts
        </PlaidLinkButton>
      </div>
    );
  }

  // Get the user's first name for the welcome message
  const firstName = user?.firstName ?? user?.name?.split(' ')?.[0];
  const greeting = firstName ? `Welcome back, ${firstName}` : 'Welcome back';

  return (
    <Entity.Pane>
      <Entity.Header title={greeting} />
      <CalloutPaneBody
        leftColumn={[
          <MonthlySpendComparisonChart key="chart" transactions={transactions} isLoading={transactionsLoading} />,
          <TransactionsTable
            key="table"
            primaryElement={TxnTableElementType.Merchant}
            secondaryElement={TxnTableElementType.Account}
          />,
        ]}
        rightColumn={<DashboardAccountsWidget />}
      />
    </Entity.Pane>
  );
};

export default DashboardPane;
